import React from "react";

import SidebarModal from "../../../Components/SidebarModal";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { Organization } from "../../AdminOverviewContainer";
import "./style.scss";

export type OrgFeatureKey =
  | "rl_assessments"
  | "weekly_digest"
  | "product_telemetrics"
  | "coverage_analysis";

export interface OrgFeatureDefinition {
  key: OrgFeatureKey;
  shortLabel: string;
  label: string;
  description: string;
  isEnabled: (org: Organization) => boolean;
  isUpdating: (tenantId: string, updatingTenantId: string | null) => boolean;
}

export const ORG_FEATURE_DEFINITIONS: OrgFeatureDefinition[] = [
  {
    key: "rl_assessments",
    shortLabel: "RL",
    label: "RL Assessments",
    description:
      "Enable vendor teams to score and track relationship strength with contacts. When disabled, Relationship workflows and navigation will be hidden for this organization.",
    isEnabled: org =>
      Boolean(org.settings?.rl_setting_config?.relationship_assessments),
    isUpdating: (tenantId, updatingTenantId) => updatingTenantId === tenantId,
  },
  {
    key: "weekly_digest",
    shortLabel: "Weekly Digest",
    label: "Weekly Digest",
    description:
      "Sends the Monday morning ritual email to executive sponsors with portfolio highlights. When off, digest delivery is blocked for this org.",
    isEnabled: org => {
      const enabled = org.settings?.weekly_digest?.enabled;
      if (enabled === undefined) return true;
      return Boolean(enabled);
    },
    isUpdating: (tenantId, updatingTenantId) => updatingTenantId === tenantId,
  },
  {
    key: "product_telemetrics",
    shortLabel: "Telemetry",
    label: "Product Telemetrics",
    description:
      "Shows product usage and engagement metrics to vendor admins on Home. When off, the Telemetry Data tab stays hidden for this org.",
    isEnabled: org => Boolean(org.settings?.product_telemetrics?.enabled),
    isUpdating: (tenantId, updatingTenantId) => updatingTenantId === tenantId,
  },
  {
    key: "coverage_analysis",
    shortLabel: "Coverage",
    label: "Coverage Analysis",
    description:
      "Unlocks the Coverage workspace on Executive Operations for mapping owner–contact coverage. When off, the Coverage tab is not shown for this org.",
    isEnabled: org => Boolean(org.settings?.coverage_analysis?.enabled),
    isUpdating: (tenantId, updatingTenantId) => updatingTenantId === tenantId,
  },
];

interface OrgFeatureUpdatingIds {
  rlAssessments: string | null;
  weeklyDigest: string | null;
  productTelemetrics: string | null;
  coverageAnalysis: string | null;
}

interface OrgFeaturesDrawerProps {
  isOpen: boolean;
  org: Organization | null;
  onClose: () => void;
  updatingIds: OrgFeatureUpdatingIds;
  onToggleFeature: (
    org: Organization,
    featureKey: OrgFeatureKey,
    toEnabled: boolean
  ) => void;
}

const updatingIdForFeature = (
  featureKey: OrgFeatureKey,
  updatingIds: OrgFeatureUpdatingIds
): string | null => {
  switch (featureKey) {
    case "rl_assessments":
      return updatingIds.rlAssessments;
    case "weekly_digest":
      return updatingIds.weeklyDigest;
    case "product_telemetrics":
      return updatingIds.productTelemetrics;
    case "coverage_analysis":
      return updatingIds.coverageAnalysis;
    default:
      return null;
  }
};

const OrgFeaturesDrawer: React.FC<OrgFeaturesDrawerProps> = ({
  isOpen,
  org,
  onClose,
  updatingIds,
  onToggleFeature,
}) => {
  if (!org) return null;

  return (
    <SidebarModal
      isOpen={isOpen}
      onClose={onClose}
      title="Feature controls"
      className="org-features-drawer"
    >
      <div className="org-features-drawer__intro">
        <p className="org-features-drawer__intro-title">
          Choose what <strong>{org.organization}</strong> can access
        </p>
        <p className="org-features-drawer__intro-body">
          Flip a switch to unlock or hide a product area for this organization.
          You&apos;ll confirm each change before it goes live.
        </p>
      </div>
      <ul className="org-features-drawer__list">
        {ORG_FEATURE_DEFINITIONS.map(feature => {
          const enabled = feature.isEnabled(org);
          const updating = feature.isUpdating(
            org.tenant_id,
            updatingIdForFeature(feature.key, updatingIds)
          );
          return (
            <li
              key={feature.key}
              className={`org-features-drawer__item ${
                enabled ? "org-features-drawer__item--on" : ""
              }`}
            >
              <div className="org-features-drawer__item-copy">
                <div className="org-features-drawer__item-heading">
                  <div className="org-features-drawer__item-title">
                    {feature.label}
                  </div>
                  <span
                    className={`org-features-drawer__status ${
                      enabled
                        ? "org-features-drawer__status--on"
                        : "org-features-drawer__status--off"
                    }`}
                  >
                    {enabled ? "On" : "Off"}
                  </span>
                </div>
                <div className="org-features-drawer__item-desc">
                  {feature.description}
                </div>
              </div>
              <ToggleSwitch
                checked={enabled}
                onChange={checked => onToggleFeature(org, feature.key, checked)}
                id={`org-feature-${feature.key}-${org.tenant_id}`}
                disabled={updating}
                className="org-features-drawer__toggle"
              />
            </li>
          );
        })}
      </ul>
    </SidebarModal>
  );
};

export default OrgFeaturesDrawer;
